import { FormInstance } from "antd";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { PartnerManagementInjection } from "features/partnerManagement/di/PartnerManagementInjection";
import { B2CPartnerActionRequestEntity } from "features/partnerManagement/domain/entities/b2cProvider/B2CPartnerActionRequestEntity";
import { B2CPartnerDetailResponseEntity } from "features/partnerManagement/domain/entities/b2cProvider/B2CPartnerDetailResponseEntity";
import { B2CPartnerRequestEntity } from "features/partnerManagement/domain/entities/b2cProvider/B2CPartnerRequestEntity";
import { B2CPartnerResponseEntity } from "features/partnerManagement/domain/entities/b2cProvider/B2CPartnerResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

class B2CPartnerInput implements Disposable {
  public fetchPartner = new BehaviorSubject<B2CPartnerRequestEntity>({});
  public loading = new BehaviorSubject<boolean>(false);
  public modalLoading = new BehaviorSubject<boolean>(false);
  public fetchDetail = new BehaviorSubject<B2CPartnerRequestEntity>({});
  public fetchActions = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    body?: B2CPartnerActionRequestEntity;
    detailInfo?: B2CPartnerDetailResponseEntity;
  }>({});

  [Symbol.dispose](): void {
    this.fetchPartner.complete();
    this.fetchActions.complete();
    this.fetchDetail.complete();
    this.loading.complete();
    this.modalLoading.complete();
  }
}

class B2CPartnerOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<B2CPartnerResponseEntity>>([]);
  public pagination = new BehaviorSubject<Paging>({});
  public savedDetail = new BehaviorSubject<B2CPartnerDetailResponseEntity>({});
  public onError = new BehaviorSubject<Error>({});
  public onUpdateSuccess = new BehaviorSubject<boolean>(false);
  public onActionsSuccess = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.pagination.complete();
    this.savedDetail.complete();
    this.onError.complete();
    this.onUpdateSuccess.complete();
    this.onActionsSuccess.complete();
  }
}

@injectable()
export class B2CPartnerManagementViewModel extends BaseViewModel<
  B2CPartnerInput,
  B2CPartnerOutput
> {
  constructor() {
    super(new B2CPartnerInput(), new B2CPartnerOutput());
  }

  private b2cPartnerUseCase = PartnerManagementInjection.getB2CUseCase();

  public buildTableQuery(
    pagination: any,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: B2CPartnerRequestEntity
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.b2cPartnerUseCase.getList(params);
        if (res?.data) {
          this.output.savedList.next(res?.data);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.b2cPartnerUseCase.getDetail({
          code: params?.code,
        });
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      let { action, body, detailInfo } = props;
      switch (action) {
        case "create":
          try {
            this.input.modalLoading.next(true);
            const res = await this.b2cPartnerUseCase?.createPartner(
              body,
              detailInfo
            );
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.CreateSuccessfully
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.modalLoading.next(false);
          }
          break;
        case "update":
          try {
            this.input.modalLoading.next(true);
            const res = await this.b2cPartnerUseCase?.updatePartner(
              body,
              detailInfo
            );
            if (res?.data) {
              this.output.onUpdateSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.modalLoading.next(false);
          }
          break;
        case "delete":
          try {
            this.input.loading.next(true);
            const res = await this.b2cPartnerUseCase?.deletePartner({
              code: body?.code,
            });
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.DeleteSuccessfully
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
          break;
      }
    });
  }
}
